/* -----------------Globals--------------- */
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";

import type { IconType } from "../../icon";
/* -----------------Types--------------- */
import type { FeedbackParams } from "./types";

import { useDevice } from "../../useDevice";
/* -----------------Child components--------------- */
import Box from "../Box";
import RenderMarkdown from "../MarkdownRenderer";
import { useToast } from "../Toast";
import { FeedbackForm } from "./FeedbackForm";
import { FeedbackHeader } from "./FeedbackHeader";
import { FeedbackReasons } from "./FeedbackReasons";

type FeedbackContentRef = {
	submit: () => void;
};

type FeedbackContentProps = {
	sendFeedback: (params: FeedbackParams, feedback: string) => void;
	feedbackDialogParams: FeedbackParams;
	onDismiss: () => void;
	subtitle: string;
	showFeedbackConfirmationToast: () => void;
	handlePostFeedback?: (
		feedback: string,
		email: string,
		selectedReasons?: string[],
	) => void;
	feedbackReasonList?: {
		title: string;
		description: string;
		icon: IconType;
		value: string;
	}[];
	onStateChange?: (sendingFeedback: boolean, isValid: boolean) => void;
	onInputFocus?: (field: "email" | "feedback") => void;
	feedbackLimitError?: string;
	feedbackPlaceholder?: string;
};

type FeedbackContentComponent = React.ForwardRefExoticComponent<
	FeedbackContentProps & React.RefAttributes<FeedbackContentRef>
> & {
	Header: typeof FeedbackHeader;
	Reasons: typeof FeedbackReasons;
	Form: typeof FeedbackForm;
};

const FeedbackContent = forwardRef<FeedbackContentRef, FeedbackContentProps>(
	(
		{
			sendFeedback,
			feedbackDialogParams,
			onDismiss,
			subtitle,
			showFeedbackConfirmationToast,
			handlePostFeedback,
			feedbackReasonList,
			onStateChange,
			onInputFocus,
			feedbackLimitError,
			feedbackPlaceholder,
		},
		ref,
	) => {
		const { isMobile } = useDevice();
		const { showToast } = useToast();
		const [sendingFeedback, setSendingFeedback] = useState(false);
		const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
		const [isEmailValid, setIsEmailValid] = useState(true);
		const [isFeedbackValid, setIsFeedbackValid] = useState(false);
		const [emailText, setEmailText] = useState("");
		const [feedbackText, setFeedbackText] = useState("");
		const formRef = useRef<{ submit: () => void } | null>(null);

		useImperativeHandle(ref, () => ({
			submit: () => {
				if (formRef.current) {
					formRef.current.submit();
				}
			},
		}));

		useEffect(() => {
			if (onStateChange) {
				const isEmailRequired = !feedbackDialogParams?.email;
				const emailValidationPassed =
					!isEmailRequired || (isEmailValid && emailText.length > 0);

				const feedbackValidationPassed =
					feedbackReasonList && feedbackReasonList.length > 0
						? selectedReasons.length > 0
						: isFeedbackValid && feedbackText.length > 0;

				onStateChange(
					sendingFeedback,
					!sendingFeedback && emailValidationPassed && feedbackValidationPassed,
				);
			}
		}, [
			sendingFeedback,
			selectedReasons,
			isEmailValid,
			isFeedbackValid,
			emailText,
			feedbackText,
			onStateChange,
			feedbackReasonList,
			feedbackDialogParams?.email,
		]);

		const orderedReasons = (reasons: string[]) =>
			feedbackReasonList
				? feedbackReasonList
						.map(({ value }) => value)
						.filter((value) => reasons.includes(value))
				: reasons;

		const handleSendFeedback = async (feedback: string, email: string) => {
			setSendingFeedback(true);

			onDismiss();
			showFeedbackConfirmationToast();
			handlePostFeedback?.(
				feedback,
				feedbackDialogParams.email ?? email,
				selectedReasons.length > 0 ? selectedReasons : undefined,
			);

			try {
				const feedbackMessage =
					selectedReasons.length > 0
						? `Selected reasons: ${selectedReasons.join(", ")} \nUser feedback: ${feedback}`
						: `User feedback: ${feedback}`;
				const finalFeedbackDialogParams = {
					...feedbackDialogParams,
					feedback: feedbackMessage,
					email: feedbackDialogParams.email || email,
				};

				await sendFeedback(finalFeedbackDialogParams, feedbackMessage);
			} catch {
				showToast({
					message: "Failed to send feedback. Please try again.",
					action: {
						label: "Retry",
						action: () => {
							handleSendFeedback(feedback, email);
						},
					},
				});
			} finally {
				setSendingFeedback(false);
			}
		};

		const handleReasonPress = (reason: string) => {
			setSelectedReasons((current) =>
				current.includes(reason)
					? current.filter((value) => value !== reason)
					: orderedReasons([...current, reason]),
			);
		};

		return (
			<Box gap={16}>
				{subtitle && isMobile && (
					<Box paddingHorizontal={24}>
						<RenderMarkdown
							text={subtitle}
							variant="explanation"
							textAlign="center"
						/>
					</Box>
				)}
				{feedbackReasonList && feedbackReasonList.length > 0 ? (
					<FeedbackReasons
						feedbackReasonList={feedbackReasonList}
						selectedReasons={selectedReasons}
						onReasonPress={handleReasonPress}
					/>
				) : null}
				<Box paddingHorizontal={24}>
					<FeedbackForm
						ref={formRef}
						feedbackLimitError={feedbackLimitError ?? ""}
						feedbackPlaceholder={feedbackPlaceholder ?? ""}
						feedbackDialogParams={feedbackDialogParams}
						feedbackReasonList={feedbackReasonList}
						onSubmit={handleSendFeedback}
						onEmailValidationChange={setIsEmailValid}
						onFeedbackValidationChange={setIsFeedbackValid}
						onEmailTextChange={setEmailText}
						onFeedbackTextChange={setFeedbackText}
						onInputFocus={onInputFocus}
					/>
				</Box>
			</Box>
		);
	},
);

FeedbackContent.displayName = "FeedbackContent";

const FeedbackContentWithComposition =
	FeedbackContent as FeedbackContentComponent;
FeedbackContentWithComposition.Header = FeedbackHeader;
FeedbackContentWithComposition.Reasons = FeedbackReasons;
FeedbackContentWithComposition.Form = FeedbackForm;

export { FeedbackContentWithComposition as FeedbackContent };
